import { Autocomplete, Checkbox, colors, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, InputAdornment, InputLabel, MenuItem, RadioGroup, Select, SxProps, TextField, TextFieldProps, Theme } from "@mui/material";
import { Editor } from "@tinymce/tinymce-react";
import { MutableRefObject, useEffect } from "react";
import { EntityStore, UseEntityStore, ValidationMessageType } from "./entityStore";
import { EnumItemDto } from "./enum";
import { DateField, NumberField } from "./form";
import { UseListStore } from "./listStore";
import { ifNull, KeysOfType, sanitize } from "./utils";
import { Property } from "csstype";
import { arrayBufferToBase64 } from "./download";
import { useConfig } from "./config";
import { sortBy } from "lodash";
import { getResource } from "./localization";
import { getInvalidCharacterText, propValueValidator } from "./entityStoreValidators";
import { unicodeName } from "unicode-name";
import { useDefaultValues } from "./defaultValues";

export const recommendedColor = colors.purple[800];

export function entityHeader(items: (null | false | [string, () => JSX.Element])[], itemsRight: (null | false | [string, () => JSX.Element])[] = []) {
    const definedItems = (items as [string, () => JSX.Element][]).filter(p => p);
    const definedItemsRight = (itemsRight as [string, () => JSX.Element][]).filter(p => p);
    return definedItems.length === 0 && definedItemsRight.length === 0 ? undefined : <Grid item container flexWrap="nowrap" mb={2}>
        <Grid item container mb={2} spacing={2} sx={{ flexGrow: 1 }}>{definedItems.map(p => <Grid item key={p[0]}>{p[1]()}</Grid>)}</Grid>
        <Grid item xs container mb={2} spacing={2} flexWrap="nowrap">{definedItemsRight.map(p => <Grid item key={p[0]}>{p[1]()}</Grid>)}</Grid>
    </Grid>;
}

export function getMostImportantValidationMessage<T>(store: EntityStore<T>, prop: keyof T): { firstMessage: string | undefined, firstMessageType: ValidationMessageType | undefined; } {
    const firstMessage = sortBy(store.propValidationMessages[prop] ?? [], p => typeof p === "object" ? p.type === "error" : true).at(0);
    return typeof firstMessage === "string" ? { firstMessage, firstMessageType: "error" } :
        typeof firstMessage === "object" ? { firstMessage: firstMessage.message, firstMessageType: firstMessage.type } :
            { firstMessage: undefined, firstMessageType: undefined };
}

export function getFirstError<T>(store: EntityStore<T>, prop: keyof T): string | undefined {
    return (store.propValidationErrors[prop] ?? []).at(0);
}

export function PropTinyMceEditor<T>(props: {
    useStore: UseEntityStore<T>;
    prop: KeysOfType<T, string | null | undefined>;
    label: string;
    disabled?: boolean;
    readOnly?: boolean;
    multiline?: boolean;
    type?: string;
    logPrint?(): void;
}) {
    const { propValue, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop],
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    const { tinyMceKey } = useConfig(p => ({ tinyMceKey: p.clientData.tinyMceKey }));
    return <FormControl error={firstMessage != null}>
        <Grid container direction="column">
            <Grid item><InputLabel>{props.label}</InputLabel></Grid>
            <Grid item>
                <Editor apiKey={tinyMceKey ?? undefined}
                    init={{
                        height: 400,
                        width: "100%",
                        menubar: false,
                        plugins: [
                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                        ],
                        toolbar: 'undo redo | formatselect | ' +
                            'bold italic backcolor | alignleft aligncenter ' +
                            'alignright alignjustify | bullist numlist outdent indent | ' +
                            'removeformat | help | print',
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    }}
                    value={propValue != null ? propValue as string : ""}
                    onEditorChange={p => props.useStore.getState().setClientDataProp(props.prop, p as T[KeysOfType<T, string | null | undefined>])}
                    onExecCommand={p => {
                        if (props.logPrint && p.command === "mcePrint") {
                            props.logPrint();
                        }
                    }}
                />
            </Grid>
            {firstMessage != null && <Grid item><FormHelperText>{firstMessage}</FormHelperText></Grid>}
        </Grid>
    </FormControl>;
}

export function propTinyMceEditor<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
    multiline?: boolean;
}) {
    return (prop: KeysOfType<T, string | null | undefined>, label: string, props?: {
        disabled?: boolean;
        readOnly?: boolean;
        multiline?: boolean;
        type?: string;
        logPrint?(): void;
    }) =>
        <PropTinyMceEditor useStore={useStore} prop={prop} label={label} {...defaultProps} {...props} />;
}

export function PropFileField<T>(props: {
    useStore: UseEntityStore<T>;
    prop: KeysOfType<T, File | null | undefined>;
    label: string;
    disabled?: boolean;
    bgColor?: string;
    onChange?(newValue: File | null | undefined, oldValue: File | null | undefined, entity: T): void;
}) {
    const { propValue, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop] as File | null | undefined,
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    return <div style={{ position: "relative" }}>
        <input type="file" style={{ position: "absolute", width: "100%", height: "100%", opacity: 0, zIndex: 1 }}
            disabled={props.disabled}
            onChange={async p => {
                const files = (p.target as HTMLInputElement).files;
                const file = files && files[0] as File;
                if (file != null) {
                    const data = arrayBufferToBase64(await file.arrayBuffer());
                    (file as any).toJSON = () => data;
                }
                props.useStore.getState().setClientDataProp(props.prop, file as T[KeysOfType<T, File | null | undefined>]);
                props.onChange?.(file, propValue, props.useStore.getState().clientData);
            }} />
        <TextField label={props.label} disabled={props.disabled} value={propValue != null ? propValue.name : ""} multiline
            style={{ backgroundColor: props.bgColor }}
            InputProps={{ readOnly: true }}
            error={firstMessage != null} helperText={firstMessage} />
    </div>;
}

export function propFileField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
}) {
    return (prop: KeysOfType<T, File | null | undefined>, label: string, props?: {
        disabled?: boolean;
        bgColor?: string;
        onChange?(newValue: File | null | undefined, oldValue: File | null | undefined, entity: T): void;
    }) =>
        <PropFileField useStore={useStore} prop={prop} label={label} {...defaultProps} {...props} />;
}

export function PropTextField<T, U extends KeysOfType<T, string | null | undefined>>(props: {
    useStore: UseEntityStore<T>;
    prop: U;
    label: string;
    disabled?: boolean;
    readOnly?: boolean;
    multiline?: boolean;
    rows?: number;
    type?: "password";
    bgColor?: string | false | null;
    onChange?(newValue: string | null | undefined, oldValue: string | null | undefined, entity: T): void;
    onBlur?(): void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    inListHeader?: boolean;
    allowAllCharacters?: boolean;
    sanitize?: boolean;
    unit?: string;
    autoFocus?: boolean;
    autocompleteValues?: string[];
}) {
    const { propValue, firstMessage, firstMessageType } = props.useStore(p => ({
        propValue: p.clientData[props.prop],
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    useGenericTextValidator(props.useStore, props.prop, props.allowAllCharacters, props.multiline);
    const endAdornment = props.unit == null ? undefined :
        <InputAdornment position="end" style={{ paddingTop: 1 }}>{props.unit}</InputAdornment>;
    const onBlur = () => {
        props.useStore.getState().setClientDataProp(props.prop, propValue == null ? propValue :
            ((props.sanitize ?? true) ? sanitize(propValue as string) : propValue) as T[KeysOfType<T, string | null | undefined>]);
        props.onBlur?.();
    };
    return props.autocompleteValues == null ? <TextField value={ifNull(propValue as string, "")} label={props.label} disabled={props.disabled}
        onKeyDown={p => {
            if (p.key === "Enter") {
                onBlur();
            }
            props.onKeyDown?.(p);
        }} autoFocus={props.autoFocus}
        InputProps={{
            readOnly: props.readOnly,
            rows: props.rows,
            sx: {
                height: props.inListHeader ? 26 : undefined,
                backgroundColor: props.bgColor || undefined
            }
        }}
        autoComplete={props.type === "password" ? "new-password" : undefined}
        sx={{
            mb: props.inListHeader ? 0 : undefined,
            ...firstMessageType === "error" ? {} :
                firstMessageType === "recommended" ? {
                    "& .MuiFormLabel-root.Mui-error": {
                        color: p => `${recommendedColor} !important`,
                    },
                    "& .Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: p => `${recommendedColor} !important`,
                    },
                    "& .MuiFormHelperText-root.Mui-error": {
                        color: p => `${recommendedColor} !important`,
                    }
                } : {},
        }}
        error={firstMessage != null} helperText={firstMessage} multiline={props.multiline} type={props.type} onChange={p => {
            props.useStore.getState().setClientDataProp(props.prop, p.target.value as T[KeysOfType<T, string | null | undefined>]);
            props.onChange?.(p.target.value, propValue as string | null | undefined, props.useStore.getState().clientData);
        }} onBlur={onBlur} /> : <Autocomplete freeSolo disableClearable={true} options={props.autocompleteValues}
            value={ifNull(propValue as string, "")} onInputChange={(e, v) => {
                props.useStore.getState().setClientDataProp(props.prop, v as T[KeysOfType<T, string | null | undefined>]);
            }} renderInput={(params) => <TextField {...params} label={props.label}
                InputProps={{ ...params.InputProps, endAdornment: endAdornment ?? params.InputProps.endAdornment }}
                style={{ backgroundColor: props.bgColor || undefined }} autoFocus={props.autoFocus} onBlur={onBlur}
                error={firstMessage != null} helperText={firstMessage} multiline={props.multiline} type={props.type}
            />} />;
}

export function useGenericTextValidator<T, U extends KeysOfType<T, string | null | undefined>>(useStore: UseEntityStore<T>, prop: U, allowAllCharacters = false, multiline = false) {
    const { allowedCharacters, allowedCharactersDict } = useDefaultValues(p => ({ allowedCharacters: p.clientData.allowedCharacters, allowedCharactersDict: p.clientData.allowedCharactersDict }))
    useEffect(() => {
        const validator = propValueValidator<T, U>(prop, p => {
            if (allowedCharacters.length === 0 || allowAllCharacters || !(typeof p === "string")) {
                return;
            }
            const isValid = (p: string) => allowedCharactersDict[p] || (multiline && p === "\n");
            const firstInvalidCharacter = p.split("").find(p => !isValid(p));
            return firstInvalidCharacter != null && getInvalidCharacterText()
                .replaceAll("{0}", firstInvalidCharacter)
                .replaceAll("{1}", unicodeName(firstInvalidCharacter));
        });
        const id = useStore.getState().addDynamicPropValidator(validator);
        return () => {
            useStore.getState().removeDynamicPropValidator(id);
        };
    }, [useStore, prop, allowedCharacters, allowedCharactersDict, allowAllCharacters, multiline]);
}

export function propTextField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
    multiline?: boolean;
    onBlur?(): void;
    inListHeader?: boolean;
    allowAllCharacters?: boolean;
    sanitize?: boolean;
}) {
    return (prop: KeysOfType<T, string | null | undefined>, label: string, props?: {
        disabled?: boolean;
        readOnly?: boolean;
        multiline?: boolean;
        rows?: number;
        type?: "password";
        bgColor?: string | false | null;
        onChange?(newValue: string | null | undefined, oldValue: string | null | undefined, entity: T): void;
        onBlur?(): void;
        onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
        inListHeader?: boolean;
        allowAllCharacters?: boolean;
        sanitize?: boolean;
        autoFocus?: boolean;
        autocompleteValues?: string[];
    }) =>
        <PropTextField useStore={useStore} prop={prop} label={label} {...defaultProps} {...props} />;
}

export const boolInputMarginTop = 0;

export function PropRadioGroup<T, V, K extends KeysOfType<T, V>>(props: {
    useStore: UseEntityStore<T>;
    prop: K;
    transformValue: (value: string) => V;
    label: string;
    disabled?: boolean;
    readOnly?: boolean;
    children: React.ReactNode;
    onChange?(newValue: V, oldValue: V, entity: T): void;
}) {
    const { propValue, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop],
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    return <FormControl error={firstMessage != null}>
        <FormLabel>{props.label}</FormLabel>
        {firstMessage != null && <FormHelperText>{firstMessage}</FormHelperText>}
        <RadioGroup value={propValue ?? ""}
            onChange={p => {
                const value = props.transformValue(p.target.value) as T[KeysOfType<T, V>];
                props.useStore.getState().setClientDataProp(props.prop, value);
                props.onChange?.(value as V, propValue as V, props.useStore.getState().clientData);
            }}
            children={props.children}
        />
    </FormControl>;
}

export function propRadioGroup<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
}) {
    return function <V>(prop: KeysOfType<T, V>, label: string, transformValue: (value: string) => V, props: {
        disabled?: boolean;
        readOnly?: boolean;
        children: React.ReactNode;
        onChange?(newValue: V, oldValue: V, entity: T): void;
    }) {
        return <PropRadioGroup useStore={useStore} prop={prop} label={label} transformValue={transformValue} {...defaultProps} {...props} />;
    };
}

function boolValueToString(value: boolean | null | undefined) {
    return value === true ? "true" :
        value === false ? "false" :
            value === null ? "null" :
                "undefined";
}

export function PropBoolField<T>(props: {
    useStore: UseEntityStore<T>;
    prop: KeysOfType<T, boolean | null | undefined>;
    label: string | null | undefined;
    disabled?: boolean;
    readOnly?: boolean;
    onChange?(newValue: boolean | null | undefined, oldValue: boolean | null | undefined, entity: T): void;
    sx?: SxProps<Theme>;
    enumValues?: { value: boolean | null | undefined, label: string }[];
    bgColor?: string;
}) {
    const { propValue, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop] as boolean | null | undefined,
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    if (props.enumValues) {
        const inputValue = boolValueToString(propValue);
        return <FormControl error={firstMessage != null}>
            <InputLabel>{props.label}</InputLabel>
            <Select value={inputValue} label={props.label} disabled={props.disabled} readOnly={props.readOnly}
                style={{ backgroundColor: props.bgColor }}
                onChange={p => {
                    const newValue = p.target.value === "true" ? true :
                        p.target.value === "false" ? false :
                            p.target.value === "null" ? null :
                                undefined;
                    props.useStore.getState().setClientDataProp(props.prop, newValue as T[KeysOfType<T, boolean | null | undefined>]);
                    if (props.onChange != null) {
                        props.onChange(newValue, propValue, props.useStore.getState().clientData);
                    }
                }}>
                {props.enumValues.map(p => <MenuItem value={boolValueToString(p.value)} key={boolValueToString(p.value)}>
                    {p.label}
                </MenuItem>)}
            </Select>
            {firstMessage != null && <FormHelperText>{firstMessage}</FormHelperText>}
        </FormControl>;
    }

    const checkbox = <Checkbox disabled={props.disabled} readOnly={props.readOnly}
        checked={ifNull(propValue, false)}
        indeterminate={propValue == null}
        onChange={p => {
            const oldValue = propValue;
            const newValue = p.target.checked as boolean | null | undefined;
            props.useStore.getState().setClientDataProp(props.prop, newValue as T[KeysOfType<T, boolean | null | undefined>]);
            if (props.onChange != null) {
                props.onChange(newValue, oldValue, props.useStore.getState().clientData);
            }
        }}
    />;
    return <FormControl error={firstMessage != null} margin="none" {...{ sx: props.sx }}>
        {props.label ? <FormControlLabel control={checkbox} label={props.label} /> : checkbox}
        {firstMessage != null && <FormHelperText>{firstMessage}</FormHelperText>}
    </FormControl>;
}

export function propBoolField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
    sx?: SxProps<Theme>;
}) {
    return (prop: KeysOfType<T, boolean | null | undefined>, label: string | null | undefined, props?: {
        disabled?: boolean;
        readOnly?: boolean;
        onChange?(newValue: boolean | null | undefined, oldValue: boolean | null | undefined, entity: T): void;
        sx?: SxProps<Theme>;
        enumValues?: { value: boolean | null | undefined, label: string }[];
        bgColor?: string;
    }) =>
        <PropBoolField useStore={useStore} prop={prop} label={label} {...defaultProps} {...props} />;
}

export function PropCustomEnumField<T, U extends KeysOfType<T, string | number | null | undefined>, V>(props: {
    useStore: UseEntityStore<T>;
    prop: U;
    label: string;
    useEnumStore: UseListStore<V>;
    disabled?: boolean;
    readOnly?: boolean;
    onChange?(newValue: T[U], oldValue: T[U], entity: T): void;
    addAll?: boolean;
    addNone?: boolean;
    noneValue?: T[U];
    noneText?: string;
    getId(item: V): string | number;
    getName(item: V): string;
    getActive?(item: V): boolean;
    bgColor?: string | false;
    autoComplete?: boolean;
    filter?: (item: V) => boolean;
    localizeKey?: string;
    displayEmpty?: boolean;
}) {
    const { propValue, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop],
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    const enumStore = props.useEnumStore();
    const inputValue = propValue != null && enumStore.items.some(p => props.getId(p) === propValue) ? propValue : "";
    const selectedItem = propValue != null ? enumStore.items.find(p => props.getId(p) === propValue) : undefined;
    const allItems = props.getActive == null ? enumStore.items : enumStore.items.filter(p => props.getId(p) === inputValue || props.getActive!(p));
    const items = props.filter == null ? allItems : allItems.filter(p => p === selectedItem || props.filter!(p));

    if (props.autoComplete || (props.autoComplete !== false && items.length > 30)) {
        return <Autocomplete
            disabled={props.disabled} readOnly={props.readOnly}
            options={items}
            value={selectedItem || null}
            getOptionLabel={p => props.getName(p)}
            onChange={(p, v) => {
                const newValue = (v == null ? props.noneValue : props.getId(v)) as T[U];
                props.useStore.getState().setClientDataProp(props.prop, newValue);
                if (props.onChange != null) {
                    props.onChange(newValue, propValue, props.useStore.getState().clientData);
                }
            }}
            renderInput={(params) => <TextField {...params}
                style={{ backgroundColor: props.bgColor || undefined }}
                error={firstMessage != null} helperText={firstMessage}
                label={props.label} />}
        />;
    }

    return <FormControl error={firstMessage != null}>
        <InputLabel>{props.label}</InputLabel>
        <Select value={inputValue} label={props.label} disabled={props.disabled} readOnly={props.readOnly}
            style={{ backgroundColor: props.bgColor || undefined }}
            displayEmpty={props.displayEmpty}
            onChange={p => {
                const newValue = (p.target.value === "" ? props.noneValue : p.target.value) as T[U];
                props.useStore.getState().setClientDataProp(props.prop, newValue);
                if (props.onChange != null) {
                    props.onChange(newValue, propValue, props.useStore.getState().clientData);
                }
            }}>
            {props.addAll && <MenuItem value="">{props.localizeKey ? getResource(props.localizeKey + "All", "(all)") : getResource("Ui.Option.All", "(all)")}</MenuItem>}
            {props.addNone && <MenuItem value="">{props.noneText ?? (props.localizeKey ? getResource(props.localizeKey + "None", "(none)") : getResource("Ui.Option.None", "(none)"))}</MenuItem>}
            {items.map(p => <MenuItem value={props.getId(p)} key={props.getId(p)}>{
                props.getName(p) === "" ? String.fromCharCode(160) : props.getName(p)
            }</MenuItem>)}
        </Select>
        {firstMessage != null && <FormHelperText>{firstMessage}</FormHelperText>}
    </FormControl>;
}

export function propCustomEnumField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
}) {
    return function <U extends KeysOfType<T, string | number | null | undefined>, V>(prop: U, label: string, useEnumStore: UseListStore<V>, props: {
        disabled?: boolean;
        readOnly?: boolean;
        onChange?(newValue: T[U], oldValue: T[U], entity: T): void;
        addAll?: boolean;
        addNone?: boolean;
        noneValue?: T[U];
        noneXtext?: string;
        getId(item: V): string | number;
        getName(item: V): string;
        getActive?(item: V): boolean;
        bgColor?: string | false;
        autoComplete?: boolean;
        filter?: (item: V) => boolean;
        localizeKey?: string;
        displayEmpty?: boolean;
    }) {
        return <PropCustomEnumField useStore={useStore} prop={prop} label={label} useEnumStore={useEnumStore} {...defaultProps} {...props} />;
    };
}

export function propEnumField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
}) {
    return function <U extends KeysOfType<T, string | number | null | undefined>, V extends EnumItemDto>(prop: U, label: string, useEnumStore: UseListStore<V>, props?: {
        disabled?: boolean;
        readOnly?: boolean;
        onChange?(newValue: T[U], oldValue: T[U], entity: T): void;
        addAll?: boolean;
        addNone?: boolean;
        noneValue?: T[U];
        noneText?: string;
        bgColor?: string | false;
        showInactive?: boolean;
        autoComplete?: boolean;
        filter?: (item: V) => boolean;
        localizeKey?: string;
        displayEmpty?: boolean;
    }) {
        return propCustomEnumField(useStore, defaultProps)(prop, label, useEnumStore, {
            ...props,
            getId: p => p.id,
            getName: p => props?.localizeKey ? getResource(props.localizeKey + p.id, p.name) as string : p.name,
            getActive: p => props?.showInactive || !p.inactive
        });
    };
}

export function PropSimpleEnumField<T, U extends KeysOfType<T, string | number | boolean | null | undefined>>(props: {
    useStore: UseEntityStore<T>;
    prop: U;
    label: string;
    enumItems: EnumItemDto[];
    disabled?: boolean;
    readOnly?: boolean;
    onChange?(newValue: T[U], oldValue: T[U], entity: T): void;
    addAll?: boolean;
    addNone?: boolean;
    noneValue?: T[U];
    bgColor?: string;
    localizeKey?: string;
}) {
    const { propValue, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop],
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    const inputValue = propValue != null && props.enumItems.some(p => p.id === propValue) ? propValue : "";
    return <FormControl error={firstMessage != null}>
        <InputLabel>{props.label}</InputLabel>
        <Select value={inputValue} label={props.label} disabled={props.disabled} readOnly={props.readOnly}
            style={{ backgroundColor: props.bgColor }}
            onChange={p => {
                const newValue = (p.target.value === "" ? props.noneValue : p.target.value) as T[U];
                props.useStore.getState().setClientDataProp(props.prop, newValue);
                if (props.onChange != null) {
                    props.onChange(newValue, propValue, props.useStore.getState().clientData);
                }
            }}>
            {props.addAll && <MenuItem value="">{props.localizeKey ? getResource(props.localizeKey + "All", "(all)") : "(all)"}</MenuItem>}
            {props.addNone && <MenuItem value="">{props.localizeKey ? getResource(props.localizeKey + "None", "(none)") : "(none)"}</MenuItem>}
            {props.enumItems.map(p => <MenuItem value={p.id} key={p.id}>{props.localizeKey ? getResource(props.localizeKey + p.id, p.name) : p.name}</MenuItem>)}
        </Select>
        {firstMessage != null && <FormHelperText>{firstMessage}</FormHelperText>}
    </FormControl>;
}

export function propSimpleEnumField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
}) {
    return <U extends KeysOfType<T, string | number | boolean | null | undefined>>(prop: U, label: string, enumItems: EnumItemDto[], props?: {
        disabled?: boolean;
        readOnly?: boolean;
        onChange?(newValue: T[U], oldValue: T[U], entity: T): void;
        addAll?: boolean;
        localizeKey?: string;
        addNone?: boolean;
        noneValue?: T[U];
        bgColor?: string;
    }) =>
        <PropSimpleEnumField useStore={useStore} prop={prop} label={label} enumItems={enumItems} {...defaultProps} {...props} />;
}

export function PropNumberField<T>(props: {
    useStore: UseEntityStore<T>;
    prop: KeysOfType<T, number | null | undefined>;
    propFormatted: KeysOfType<T, string | undefined> | null;
    label: string;
    disabled?: boolean;
    readOnly?: boolean;
    isCurrency?: boolean;
    currency?: string;
    unit?: string;
    textAlign?: Property.TextAlign;
    transformValue?(value: number | null | undefined): number | null | undefined;
    onChange?(newValue: number | null | undefined, oldValue: number | null | undefined, entity: T): void;
    inputRef?: MutableRefObject<HTMLInputElement | undefined>;
    showErrorText?: boolean;
    onBlur?(): void;
    style?: TextFieldProps["style"];
    sx?: SxProps<Theme>;
    confirmChange?(newValue: number | null): Promise<boolean>;
    showTooltipError?: boolean;
    autoFocus?: boolean;
    endAdornment?: React.ReactNode;
    autocompleteValues?: number[];
}) {
    const { propValue, propValueFormatted, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop] as number | null,
        propValueFormatted: props.propFormatted == null ? null : p.clientData[props.propFormatted],
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    return <NumberField {...props} error={firstMessage} value={propValue} valueFormatted={propValueFormatted as string | null} setValue={(value, valueFormatted) => {
        props.useStore.getState().setClientDataProp(props.prop, value as T[KeysOfType<T, number | null | undefined>]);
        if (props.propFormatted) {
            props.useStore.getState().setClientDataProp(props.propFormatted, valueFormatted as T[KeysOfType<T, string | undefined>]);
        }
        if (props.onChange != null) {
            props.onChange(value, propValue, props.useStore.getState().clientData);
        }
    }} autocompleteValues={props.autocompleteValues} autoFocus={props.autoFocus} />;
}

export function propNumberField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
    isCurrency?: boolean;
    currency?: string;
    unit?: string;
    textAlign?: Property.TextAlign;
    transformValue?(value: number | null | undefined): number | null | undefined;
    onChange?(newValue: number | null | undefined, oldValue: number | null | undefined, entity: T): void;
    inputRef?: MutableRefObject<HTMLInputElement | undefined>;
    showErrorText?: boolean;
}) {
    return (prop: KeysOfType<T, number | null | undefined>, propFormatted: KeysOfType<T, string | undefined> | null, label: string, props?: {
        disabled?: boolean;
        readOnly?: boolean;
        isCurrency?: boolean;
        currency?: string;
        unit?: string;
        textAlign?: Property.TextAlign;
        transformValue?(value: number | null | undefined): number | null | undefined;
        onChange?(newValue: number | null | undefined, oldValue: number | null | undefined, entity: T): void;
        inputRef?: MutableRefObject<HTMLInputElement | undefined>;
        showErrorText?: boolean;
        onBlur?(): void;
        style?: React.CSSProperties;
        sx?: SxProps<Theme>;
        confirmChange?(newValue: number | null): Promise<boolean>;
        showTooltipError?: boolean;
        autoFocus?: boolean;
        endAdornment?: React.ReactNode;
        autocompleteValues?: number[];
    }) =>
        <PropNumberField useStore={useStore} prop={prop} propFormatted={propFormatted} label={label} {...defaultProps} {...props} />;
}

export function PropDateField<T>(props: {
    useStore: UseEntityStore<T>;
    prop: KeysOfType<T, string | null | undefined>;
    propFormatted: KeysOfType<T, string | undefined> | null;
    label: string;
    disabled?: boolean;
    readOnly?: boolean;
    type?: "datetime" | "month";
}) {
    const { propValue, propValueFormatted, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop], propValueFormatted: props.propFormatted == null ? null : p.clientData[props.propFormatted],
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    return <DateField {...props} error={firstMessage} value={propValue as string | null} valueFormatted={propValueFormatted as string | null} setValue={(value, valueFormatted) => {
        props.useStore.getState().setClientDataProp(props.prop, value as T[KeysOfType<T, string | null | undefined>]);
        if (props.propFormatted) {
            props.useStore.getState().setClientDataProp(props.propFormatted, valueFormatted as T[KeysOfType<T, string | undefined>]);
        }
    }} />;
}

export function propDateField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
}) {
    return (prop: KeysOfType<T, string | null | undefined>, propFormatted: KeysOfType<T, string | undefined> | null, label: string, props?: {
        disabled?: boolean;
        readOnly?: boolean;
        type?: "datetime" | "month";
    }) =>
        <PropDateField useStore={useStore} prop={prop} propFormatted={propFormatted} label={label} {...defaultProps} {...props} />;
}

export function PropCustomMultiEnumField<T, K extends KeysOfType<T, string[] | number[] | null | undefined>, V>(props: {
    useStore: UseEntityStore<T>;
    prop: K;
    label: string;
    useEnumStore: UseListStore<V>;
    disabled?: boolean;
    readOnly?: boolean;
    onChange?(newValue: T[K], oldValue: T[K], entity: T): void;
    getId(item: V): string | number;
    getName(item: V): string;
    bgColor?: string;
    autoComplete?: boolean;
    filter?: (item: V) => boolean;
    emptyLabel?: string;
    inListHeader?: boolean;
}) {
    const { propValue, firstMessage } = props.useStore(p => ({
        propValue: p.clientData[props.prop] as (string | number)[] | null | undefined,
        firstError: p.propValidationErrors[props.prop]?.[0],
        ...getMostImportantValidationMessage(p, props.prop),
    }));
    const selectedIds = propValue ?? [];
    const enumStore = props.useEnumStore();
    const selectedItems = enumStore.items
        .filter(p => selectedIds.includes(props.getId(p)));
    const filter = props.filter ?? (() => true);
    const allItems = enumStore.items.filter(p => selectedIds.includes(props.getId(p)) || filter(p));
    if (props.autoComplete || (props.autoComplete !== false && allItems.length > 30)) {
        return <Autocomplete
            multiple
            disabled={props.disabled} readOnly={props.readOnly}
            options={allItems}
            value={selectedItems}
            getOptionLabel={p => props.getName(p)}
            onChange={(p, v) => {
                const newValue = v.map(p => props.getId(p)) as T[K];
                props.useStore.getState().setClientDataProp(props.prop, newValue);
                if (props.onChange != null) {
                    props.onChange(newValue, selectedIds as T[K], props.useStore.getState().clientData);
                }
            }}
            renderInput={(params) => <TextField {...params}
                style={{ backgroundColor: props.bgColor }}
                error={firstMessage != null} helperText={firstMessage}
                label={props.label} />}
        />;
    }

    return <FormControl error={firstMessage != null}>
        <InputLabel>{props.label}</InputLabel>
        <Select value={selectedIds} label={props.label} disabled={props.disabled} readOnly={props.readOnly}
            style={{ backgroundColor: props.bgColor }}
            multiple displayEmpty sx={{ height: props.inListHeader ? 26 : undefined }}
            renderValue={selectedIds.length === 0 && props.emptyLabel != null ? () => <em>{props.emptyLabel}</em> : undefined}
            onChange={p => {
                const newValue = p.target.value as T[K];
                props.useStore.getState().setClientDataProp(props.prop, newValue);
                if (props.onChange != null) {
                    props.onChange(newValue, selectedIds as T[K], props.useStore.getState().clientData);
                }
            }}>
            {allItems.map(p => <MenuItem value={props.getId(p)} key={props.getId(p)}>{
                props.getName(p) === "" ? String.fromCharCode(160) : props.getName(p)
            }</MenuItem>)}
        </Select>
        {firstMessage != null && <FormHelperText>{firstMessage}</FormHelperText>}
    </FormControl>;
}

export function propCustomMultiEnumField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
}) {
    return function <K extends KeysOfType<T, string[] | number[] | null | undefined>, V>(prop: K, label: string, useEnumStore: UseListStore<V>, props: {
        disabled?: boolean;
        readOnly?: boolean;
        onChange?(newValue: T[K], oldValue: T[K], entity: T): void;
        getId(item: V): string | number;
        getName(item: V): string;
        bgColor?: string;
        autoComplete?: boolean;
        filter?: (item: V) => boolean;
        emptyLabel?: string;
        inListHeader?: boolean;
    }) {
        return <PropCustomMultiEnumField useStore={useStore} prop={prop} label={label} useEnumStore={useEnumStore} {...defaultProps} {...props} />;
    };
}

export function propMultiEnumField<T>(useStore: UseEntityStore<T>, defaultProps?: {
    disabled?: boolean;
    readOnly?: boolean;
}) {
    return function <K extends KeysOfType<T, string[] | number[] | null | undefined>, V extends { id: string | number; name: string; }>(prop: K, label: string, useEnumStore: UseListStore<V>, props?: {
        disabled?: boolean;
        readOnly?: boolean;
        onChange?(newValue: T[K], oldValue: T[K], entity: T): void;
        getId?(item: V): string | number;
        getName?(item: V): string;
        bgColor?: string;
        autoComplete?: boolean;
        filter?: (item: V) => boolean;
        emptyLabel?: string;
        inListHeader?: boolean;
    }) {
        return propCustomMultiEnumField(useStore, defaultProps)(prop, label, useEnumStore, {
            ...props,
            getId: props?.getId ?? (p => p.id),
            getName: props?.getName ?? (p => p.name),
        });
    };
}
